/**
 * Funnel UI Updater
 * 
 * Handles updating UI elements from quiz data, including prepopulation
 * from URL query parameters and form state synchronization.
 */

window.SubscriptionFunnel.UIUpdater = {
  // Update generic form inputs (text, radio, checkbox)
  updateGenericFormInputs() {
    Object.entries(quizData).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined) return;
      
      // Handle text inputs (dog name, email, etc.)
      const textInput = document.querySelector(`input[name="${key}"]`);
      if (textInput && textInput.type === 'text') {
        textInput.value = value;
        textInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
      
      // Handle radio buttons
      const radioInput = document.querySelector(`input[name="${key}"][value="${value}"]`);
      if (radioInput && radioInput.type === 'radio') {
        radioInput.checked = true;
        radioInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      
      // Handle checkboxes
      const checkboxInput = document.querySelector(`input[name="${key}"]`);
      if (checkboxInput && checkboxInput.type === 'checkbox') {
        checkboxInput.checked = Boolean(value);
        checkboxInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  },

  // Update breed inputs (special handling for different field names)
  updateBreedInputs() {
    if (quizData.breed1) {
      const breed1Input = document.querySelector('input[name="subscription_dog_breed[dog_breed_1_name]"]');
      if (breed1Input) {
        breed1Input.value = quizData.breed1;
        breed1Input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
    
    if (quizData.breed2) {
      const breed2Input = document.querySelector('input[name="subscription_dog_breed[dog_breed_2_name]"]');
      if (breed2Input) {
        breed2Input.value = quizData.breed2;
        breed2Input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  },

  // Update birthday input (special handling for different field name)
  updateBirthdayInput() {
    if (quizData.dogBirthday) {
      const birthdayInput = document.querySelector('input[name="subscription_dog_birthday[dog_birthday_year_month]"]');
      if (birthdayInput) {
        birthdayInput.value = quizData.dogBirthday;
        birthdayInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  },

  // Update email input (special handling for different field name)
  updateEmailInput() {
    if (quizData.email) {
      const emailInput = document.querySelector('input[name="subscription_email[email]"]');
      if (emailInput) {
        emailInput.value = quizData.email;
        emailInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  },

  // Update dog name display elements throughout the funnel
  updateDogNameDisplay() {
    if (quizData.dogName) {
      document.querySelectorAll("[data-dog-name]").forEach((el) => {
        el.textContent = quizData.dogName;
      });
      document.querySelectorAll("[data-dog-name-uppercase]").forEach((el) => {
        el.textContent = quizData.dogName.toUpperCase();
      });
    }
  },

  // Update commitment length selection (has multiple possible IDs across funnel versions)
  updateCommitmentLengthSelection() {
    const commitmentLengthMap = {
      '1': ['1_month'],
      '3': ['3_month'],
      '6': ['6_month'],
      '12': ['12_month']
    };
    
    const possibleIds = commitmentLengthMap[quizData.commitmentLength] || [];
    
    // Try to find and click the commitment length element
    let found = false;
    for (const id of possibleIds) {
      const element = document.getElementById(id);
      if (element && !element.checked) {
        // Set the checked state first
        element.checked = true;
        element.click();
      }
    }
  },

  // Update plan tier selection
  updatePlanTierSelection() {
    if (!quizData.planTier) return;

    // Try to find the plan tier radio button by value first
    const planTierInput = document.querySelector(`input[name="planTier"][value="${quizData.planTier}"]`);
    if (planTierInput) {
      planTierInput.checked = true;
      // Trigger the proper event handler for plan tier updates
      if (window.SubscriptionFunnel && window.SubscriptionFunnel.StepPlanSelection) {
        window.SubscriptionFunnel.StepPlanSelection.handlePlanTierSelectionClick({ target: planTierInput });
      }
      planTierInput.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    // Fallback to ID-based approach for different funnel versions
    const planTierMap = {
      'love': ['love', 'love-v2'],
      'obsessed': ['obsessed', 'obsessed-v2'],
      'super-obsessed': ['super-obsessed', 'super-obsessed-v2'],
      'toy_only': ['toy-only', 'toy-only-v2'],
      'toy_only extra_toy': ['toy-only-extra-toy', 'toy-only-extra-toy-v2'],
      'double_deluxe extra_toy': ['double-deluxe-extra-toy', 'double-deluxe-extra-toy-v2']
    };
    
    const possibleIds = planTierMap[quizData.planTier] || [];
    possibleIds.forEach(id => {
      const element = document.getElementById(id);
      if (element) {
        element.checked = true;
        // Trigger the proper event handler for plan tier updates
        if (window.SubscriptionFunnel && window.SubscriptionFunnel.StepPlanSelection) {
          window.SubscriptionFunnel.StepPlanSelection.handlePlanTierSelectionClick({ target: element });
        }
        element.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  },

  // Update dog size selection
  updateDogSizeSelection() {
    if (!quizData.dogSize) return;

    const sizeMap = {
      'small': ['small', 'dog-size-small'],
      'medium': ['medium', 'dog-size-medium'],
      'large': ['large', 'dog-size-large']
    };
    
    const possibleIds = sizeMap[quizData.dogSize] || [];
    possibleIds.forEach(id => {
      const element = document.querySelector(`input[name="dogSize"][value="${quizData.dogSize}"]`) || 
                     document.getElementById(id);
      if (element) {
        element.checked = true;
        element.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  },

  // Update dog gender selection
  updateDogGenderSelection() {
    if (!quizData.dogGender) return;

    const genderMap = {
      'male': ['male', 'dog-gender-male'],
      'female': ['female', 'dog-gender-female']
    };
    
    const possibleIds = genderMap[quizData.dogGender] || [];
    possibleIds.forEach(id => {
      const element = document.querySelector(`input[name="dogGender"][value="${quizData.dogGender}"]`) || 
                     document.getElementById(id);
      if (element) {
        element.checked = true;
        element.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  },

  // Update allergies selection
  updateAllergiesSelection() {
    // Handle specific allergy selections
    const allergyMappings = {
      'allergiesChicken': 'allergy-chicken',
      'allergiesTurkey': 'allergy-turkey', 
      'allergiesBeef': 'allergy-beef'
    };

    Object.entries(allergyMappings).forEach(([quizKey, elementId]) => {
      if (quizData[quizKey] === "true" || quizData[quizKey] === true) {
        const input = document.getElementById(elementId);
        if (input && !input.checked) {
          input.click();
        }
      }
    });

    // Handle the general "allergies" boolean parameter
    if (quizData.allergies !== null && quizData.allergies !== undefined) {
      const noExclusionsInput = document.getElementById('allergy-none');
      if (noExclusionsInput) {
        const shouldBeChecked = !quizData.allergies;
        if (noExclusionsInput.checked !== shouldBeChecked) {
          noExclusionsInput.click();
        }
      }
    }
  },

  // Update prepaid selection
  updatePrepaidSelection() {
    if (quizData.prepaid === null || quizData.prepaid === undefined) return;

    // Convert boolean to payment option value
    const paymentValue = quizData.prepaid ? 'prepaid' : 'monthly';
    
    const paymentInput = document.querySelector(`input[name="paymentOption"][value="${paymentValue}"]`);
    
    if (paymentInput) {
      paymentInput.checked = true;
      // Trigger the proper event handler for payment option updates
      if (window.SubscriptionFunnel && window.SubscriptionFunnel.StepPaymentOption) {
        window.SubscriptionFunnel.StepPaymentOption.handlePaymentOptionInputChange({ target: paymentInput });
      }
      // Also trigger input and click events to ensure visual updates
      paymentInput.dispatchEvent(new Event('input', { bubbles: true }));
      paymentInput.dispatchEvent(new Event('change', { bubbles: true }));
      paymentInput.dispatchEvent(new Event('click', { bubbles: true }));
    }
  },

  // Main function to update UI elements from quiz data
  updateUIFromQuizData() {
    // Update generic form inputs first
    this.updateGenericFormInputs();
    
    // Update special field inputs
    this.updateBreedInputs();
    this.updateBirthdayInput();
    this.updateEmailInput();
    
    // Update step-specific selections
    this.updateDogNameDisplay();
    this.updateDogSizeSelection();
    this.updateDogGenderSelection();
    this.updateAllergiesSelection();
    
    // Add delay for payment option that might not be immediately available
    setTimeout(() => {
      this.updatePrepaidSelection();
    }, 1000);

    // Send Rudderstack event for UI prepopulation
     Shopify.analytics.publish("rudderstack_track", {
       eventName: "Funnel UI Prepopulated",
       eventData: {
         ...quizData,
         prepopulationSource: "query_parameters",
         url: window.location.href,
         referrer: document.referrer || null
       }
     });
  },

  // Query parameter configuration
  getQueryParamConfig() {
    return {
      mapping: {
        'dog_name': 'dogName',
        'dog_gender': 'dogGender', 
        'dog_size': 'dogSize',
        'dog_birthday': 'dogBirthday',
        'allergies': 'allergies',
        'allergy_chicken': 'allergiesChicken',
        'allergy_turkey': 'allergiesTurkey',
        'allergy_beef': 'allergiesBeef',
        'email': 'email',
        'breed1': 'breed1',
        'breed2': 'breed2',
        'plan_tier': 'planTier',
        'assortment': 'assortment',
        'commitment_length': 'commitmentLength',
        'extra_toy': 'extraToy',
        'gwp': 'gwp',
        'prepaid': 'prepaid'
      },
      validValues: {
        'dogGender': ['male', 'female'],
        'dogSize': ['small', 'medium', 'large'],
        'planTier': ['love', 'obsessed', 'super-obsessed', 'toy_only', 'toy_only extra_toy', 'lite', 'double_deluxe extra_toy'],
        'assortment': ['toysOnly', 'toysAndTreats'],
        'commitmentLength': ['1', '3', '6', '12'],
        'extraToy': ['true', 'false', ''],
        'prepaid': ['true', 'false']
      },
      booleanFields: ['allergies', 'prepaid']
    };
  },

  // Process a single query parameter value
  processQueryParamValue(value, quizKey, config) {
    let processedValue = value;
    
    // Handle boolean conversion for specific fields
    if (config.booleanFields.includes(quizKey)) {
      processedValue = value.toLowerCase() === 'true' || value === '1';
    }
    
    return processedValue;
  },

  // Validate a processed value against allowed options
  validateQueryParamValue(value, quizKey, config) {
    const validValues = config.validValues[quizKey];
    
    if (validValues) {
      // For boolean fields, also check the string versions
      if (config.booleanFields.includes(quizKey)) {
        const stringValue = value.toString();
        const booleanValue = value === true || value === 'true' || value === '1';
        if (!validValues.includes(value) && !validValues.includes(stringValue) && !validValues.includes(booleanValue)) {
          console.warn(`Invalid value "${value}" for ${quizKey}. Valid values: ${validValues.join(', ')}`);
          return false;
        }
      } else if (!validValues.includes(value)) {
        console.warn(`Invalid value "${value}" for ${quizKey}. Valid values: ${validValues.join(', ')}`);
        return false;
      }
    }
    
    return true;
  },

  // Set a quiz data value
  setQuizDataValue(quizKey, value, queryKey) {
    if (quizKey === "dogName") {
      value = SubscriptionFunnel.FunnelUtilities.capitalizeDogName(value);
    }

    quizData[quizKey] = value;
  },

  // Process commitment length special case
  // Note: commitmentLength is used as both string and number throughout the codebase
  // We keep it as a string since that's how it's stored in quizData, but it gets converted to Number() when needed
  processCommitmentLength() {
    if (quizData.commitmentLength && !isNaN(quizData.commitmentLength)) {
      // Ensure it's a string (as expected by the codebase)
      quizData.commitmentLength = quizData.commitmentLength.toString();
    }
  },

  // Main function to capture URL query parameters and populate quiz data
  // 
  // Usage: Add query parameters to the URL to pre-populate quiz data
  // Example: /subscription-funnel/your-funnel?dog_name=Buddy&dog_size=large&allergies=true&commitment_length=6
  // 
  // Supported parameters:
  // - dog_name: Dog's name (string)
  // - dog_gender: 'male' or 'female'
  // - dog_size: 'small', 'medium', or 'large'
  // - dog_birthday: Dog's birthday (string)
  // - allergies: 'true' or 'false' (boolean)
  // - email: User's email (string)
  // - breed1: Primary breed (string)
  // - breed2: Secondary breed (string)
  // - plan_tier: 'love', 'obsessed', 'super-obsessed', 'toy_only', or 'lite'
  // - assortment: 'toysOnly' or 'toysAndTreats'
  // - commitment_length: '1', '3', '6', or '12'
  // - extra_toy: 'true' or 'false'
  // - gwp: Gift with purchase ID (string)
  // - prepaid: 'true' or 'false'
  captureQueryParamsAndPopulateQuizData() {
    const urlParams = new URLSearchParams(window.location.search);
    const config = this.getQueryParamConfig();
    
    // Process each query parameter
    Object.entries(config.mapping).forEach(([queryKey, quizKey]) => {
      const paramValue = urlParams.get(queryKey);
      
      if (paramValue !== null && paramValue !== '') {
        const processedValue = this.processQueryParamValue(paramValue, quizKey, config);
        
        if (this.validateQueryParamValue(processedValue, quizKey, config)) {
          this.setQuizDataValue(quizKey, processedValue, queryKey);
        }
      }
    });

    // Handle special cases
    this.processCommitmentLength();
    
    // Special handling for allergies: if any specific allergy params exist, set allergies to true
    const hasSpecificAllergies = urlParams.has('allergy_chicken') || 
                                urlParams.has('allergy_turkey') || 
                                urlParams.has('allergy_beef');
    
    // Also check if any specific allergies are already set to true in quizData
    const hasSpecificAllergiesInQuizData = (quizData.allergiesChicken === "true" || quizData.allergiesChicken === true) ||
                                         (quizData.allergiesTurkey === "true" || quizData.allergiesTurkey === true) ||
                                         (quizData.allergiesBeef === "true" || quizData.allergiesBeef === true);
    
    if ((hasSpecificAllergies || hasSpecificAllergiesInQuizData) && quizData.allergies !== true) {
      quizData.allergies = true;
    }
    
    // Update UI to reflect the prepopulated data
    this.updateUIFromQuizData();

    document.dispatchEvent(
      new CustomEvent("SubscriptionFunnel:QuizDataReady", {
        detail: { source: "query_parameters" }
      })
    );
  }

};
